// draft-to-evaluation-detail.ts (gh-459) — the PURE offline-read transform.
//
// Reconstructs the `EvaluationDetail` shape the resume route hydrates from (via
// `EvaluationDraftProvider` → `hydrateDraftState`) out of a device-local `drafts` row, so an
// OFFLINE reopen of `/evaluations/:id` renders the stepper from IndexedDB instead of stranding on
// "Loading evaluation…". Consumed by `useGetEvaluation`'s queryFn fallback when the network GET
// fails but a local draft exists.
//
// PURE (no Dexie, no React, no network) so it unit-tests directly and can run in the queryFn.

import type { DraftRecord } from "./idb-schema-def";
import type { EvaluationDetail, EvaluationDraftData } from "@/hooks/queries/evaluation-types";

/**
 * Map a local `drafts` row to the `EvaluationDetail` the draft store hydrates from.
 *
 *  - `clinician_id: ""` — `hydrateDraftState` reads only `{id, client_id, state, version,
 *    draft_data}` and never touches `clinician_id`, so an empty string is inert.
 *  - `client_id: row.client_id ?? ""` — persisted at CREATE / back-filled on the online GET.
 *    A legacy pre-fix row (no `client_id`) still hydrates and renders; the name-reveal is inert
 *    offline anyway (names default hidden; reveal needs the network), so `""` never crashes.
 *  - `draft_data: row.data` — the local draft body is the offline-first source of truth.
 */
export function draftRecordToEvaluationDetail(row: DraftRecord): EvaluationDetail {
  return {
    id: row.id,
    clinician_id: "",
    client_id: row.client_id ?? "",
    state: row.state,
    version: row.version,
    created_at: row.created_at,
    updated_at: row.updated_at,
    // `hydrateDraftState` is partial-data-tolerant and EVERY `EvaluationDraftData` field is optional,
    // so a single `as` cast is enough AND keeps the type-checker engaged: if the shape ever gains a
    // required field this line breaks at compile time instead of silently passing malformed IDB data
    // on the clinical resume path (PR #468 review — a double `as unknown as` cast would suppress that).
    draft_data: row.data as EvaluationDraftData,
  };
}
